import React, { useEffect, useState, useMemo } from 'react'
import { Leader, RequestProps, User, UserFormValues } from '../../store/types'
import { Controller, Control, UseFormSetValue } from 'react-hook-form'
import { Stack, FormControlLabel, Typography, Tooltip, Checkbox } from '@mui/material'
import { ExtraSmallSquareButton } from '../../styles/ButtonStyles'
import { RestartAlt } from '@mui/icons-material'
import { t } from 'i18next'
import { useGetLightLeadersQuery } from '../../store/redux/reducers/usersSlice'
import InfiniteScroll from 'react-infinite-scroll-component'
import RoleType from '../../store/types/enums/RoleType'

type ProfileViewLeaderSectionProps = {
  control: Control<UserFormValues, any>
  setValue: UseFormSetValue<UserFormValues>
  user: User | null
}

const ProfileViewLeaderSection = ({ control, setValue, user }: ProfileViewLeaderSectionProps) => {
  const [selectedLeaders, setSelectedLeaders] = useState<Leader[]>(user?.leaders || [])
  const [displayedLeaders, setDisplayedLeaders] = useState<User[]>([])
  const [hasMore, setHasMore] = useState<boolean>(true)
  const [leaderProps, setLeaderProps] = useState<RequestProps>({
    page: 0,
    size: 10,
    'roles.name': RoleType.Leader,
    employeeStatus: 'ACTIVE',
  })

  const { data: leaders } = useGetLightLeadersQuery(leaderProps)

  const handleLeaderToggle = (e: React.ChangeEvent<HTMLInputElement>, leader: Leader) => {
    let updatedSelectedLeaders: Leader[]

    if (e.target.checked) {
      updatedSelectedLeaders = [...(selectedLeaders || []), leader]
    } else {
      updatedSelectedLeaders = (selectedLeaders || []).filter(l => l.id !== leader.id)
    }

    setSelectedLeaders(updatedSelectedLeaders)
    setValue('leaders', updatedSelectedLeaders, { shouldDirty: true })
  }

  const handleResetLeaders = () => {
    setSelectedLeaders(user?.leaders || [])
  }

  const handleFetchMoreData = () => {
    setLeaderProps(prev => ({ ...prev, page: prev.page + 1 }))
  }

  useMemo(() => {
    if (leaders) {
      setHasMore(!leaders.last)
      setDisplayedLeaders(prev => [...prev].concat(leaders.content).filter(l => l.id !== user?.id))
    }
  }, [leaders, user])

  useEffect(() => {
    if (leaders && leaders.last) {
      setHasMore(false)
    }
  }, [leaders])

  return (
    <Controller
      name="leaders"
      control={control}
      render={() => (
        <Stack>
          <Stack justifyContent="space-between" direction="row" width="100%">
            <Typography variant="h5" color="text.highEmphasis">
              {t('page.usersProfile.form.headers.leader')}
            </Typography>
            <Tooltip title={t('page.usersProfile.form.headers.resetLeader')}>
              <ExtraSmallSquareButton color="secondary" onClick={handleResetLeaders}>
                <RestartAlt />
              </ExtraSmallSquareButton>
            </Tooltip>
          </Stack>
          <Stack
            sx={{
              height: displayedLeaders.length <= 4 ? 'fit-content' : 175,
              overflowY: 'auto',
              overflowX: 'hidden',
              marginTop: '16px',
              ml: '-11px',
            }}
            id="scrollableDiv"
          >
            <InfiniteScroll
              dataLength={displayedLeaders.length}
              next={handleFetchMoreData}
              hasMore={hasMore}
              loader={
                <span>
                  <i>{t('components.infiniteScroll.loading')}</i>
                </span>
              }
              scrollableTarget="scrollableDiv"
              style={{
                paddingRight: '10px',
                display: 'flex',
                flexDirection: 'column',
                overflowX: 'hidden',
              }}
            >
              {displayedLeaders?.map(l => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={selectedLeaders?.some(leader => leader.id === l.id)}
                      onChange={e => handleLeaderToggle(e, l)}
                    />
                  }
                  sx={{
                    alignSelf: 'start',
                    ml: '0px',
                    minWidth: 0,
                    width: '100%',
                    overflow: 'hidden',
                    '& .MuiFormControlLabel-label': { minWidth: 0, overflow: 'hidden' },
                  }}
                  label={
                    <Tooltip title={`${l.firstName} ${l.lastName}`} placement="top">
                      <Typography
                        sx={{
                          width: '100%',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                        }}
                      >
                        {l.firstName} {l.lastName}
                      </Typography>
                    </Tooltip>
                  }
                  key={`leader-checkbox-${l.id}`}
                />
              ))}
            </InfiniteScroll>
          </Stack>
        </Stack>
      )}
    />
  )
}

export default ProfileViewLeaderSection
